(() => {
  if (window.__vzCartEssential) return;
  window.__vzCartEssential = true;

  const ROOT = '[data-velzeen-cart]';
  const ROW = '.cart-items__table-row';
  const CONTROL = '.quantity-minus, .quantity-plus, .cart-items__remove';
  const route = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;

  const quantity = (row) => {
    const value = Number(row.querySelector('.cart-items__quantity input')?.value || 1);
    return Number.isInteger(value) && value > 0 ? value : 1;
  };

  const maximum = (row) => {
    const selector = row.querySelector('.quantity-selector');
    if (selector?.dataset.cartInventoryTracked !== 'true') return 4;
    const stock = Number(selector.dataset.cartInventoryQuantity || 0);
    return Number.isFinite(stock) ? Math.max(0, Math.min(4, stock)) : 4;
  };

  const isBusy = (root) => (
    root.dataset.vzCartBusy === 'true'
    || root.dataset.vzCartEnhancementBusy === 'true'
  );

  const emitState = (root, busy) => {
    root.dispatchEvent(new CustomEvent('velzeen:cart-line-state', {
      bubbles: true,
      detail: { owner: 'essential', busy },
    }));
  };

  const setLineBusy = (root, busy) => {
    if (busy) root.dataset.vzCartBusy = 'true';
    else delete root.dataset.vzCartBusy;
    emitState(root, busy);
  };

  const setDisabled = (element, disabled) => {
    if (!(element instanceof HTMLButtonElement)) return;
    element.disabled = disabled;
    element.setAttribute('aria-disabled', String(disabled));
    element.tabIndex = disabled ? -1 : 0;
  };

  const initialise = (root) => {
    if (!(root instanceof HTMLElement)) return;
    root.dataset.cartLineOwner = 'essential';

    const busy = isBusy(root);
    root.querySelectorAll(ROW).forEach((row) => {
      const current = quantity(row);
      setDisabled(row.querySelector('.quantity-minus'), busy || current <= 1);
      setDisabled(row.querySelector('.quantity-plus'), busy || current >= maximum(row));
      setDisabled(row.querySelector('.cart-items__remove'), busy);
    });
  };

  const initialiseAll = (scope = document) => {
    const roots = scope instanceof Element && scope.matches(ROOT)
      ? [scope]
      : scope.querySelectorAll?.(ROOT) || [];
    roots.forEach(initialise);
  };

  const showError = (root, message) => {
    root.dataset.cartRecoveryOwner = 'essential';

    const recovery = root.querySelector('[data-cart-recovery]');
    const copy = root.querySelector('[data-cart-recovery-message]');
    if (copy) copy.textContent = message;
    if (recovery instanceof HTMLElement) recovery.hidden = false;

    const status = root.querySelector('[data-cart-status]');
    if (status) status.textContent = message;
  };

  const refresh = async (root) => {
    const sectionId = root.dataset.sectionId || '';
    if (!sectionId) return false;

    try {
      const url = new URL(location.href);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return false;

      const html = await response.text();
      const replacement = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(ROOT);

      if (!(replacement instanceof HTMLElement) || !root.isConnected) return false;
      root.replaceWith(replacement);
      initialise(replacement);
      return true;
    } catch {
      return false;
    }
  };

  const change = async (root, row, line, next) => {
    setLineBusy(root, true);
    initialise(root);

    try {
      const response = await fetch(route('cart/change.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity: next }),
      });
      const cart = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(cart.description || cart.message || 'Couldn’t update Bag');
      }

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: root.id || 'velzeen-cart',
          data: {
            source: 'velzeen-cart-essential',
            itemCount: Number(cart.item_count || 0),
          },
        },
      }));

      if (!(await refresh(root))) location.assign(route('cart'));
    } catch (error) {
      setLineBusy(root, false);
      initialise(root);
      showError(root, error instanceof Error ? error.message : 'Couldn’t update Bag');
    }
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const retryButton = event.target.closest('[data-cart-recovery-retry]');
    if (retryButton) {
      const root = retryButton.closest(ROOT);
      if (!(root instanceof HTMLElement) || root.dataset.cartRecoveryOwner !== 'essential') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      location.reload();
      return;
    }

    const control = event.target.closest(CONTROL);
    if (!(control instanceof HTMLButtonElement)) return;

    const root = control.closest(ROOT);
    const row = control.closest(ROW);
    if (!(root instanceof HTMLElement) || !(row instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (isBusy(root)) return;

    const line = [...root.querySelectorAll(ROW)].indexOf(row) + 1;
    const current = quantity(row);
    const remove = control.classList.contains('cart-items__remove');
    const next = remove
      ? 0
      : control.classList.contains('quantity-plus')
        ? current + 1
        : current - 1;

    if (line < 1 || (next < 1 && !remove) || next > maximum(row)) return;
    void change(root, row, line, next);
  }, true);

  document.addEventListener('velzeen:cart-enhancement-state', (event) => {
    const root = event.target instanceof Element ? event.target.closest(ROOT) : null;
    if (root instanceof HTMLElement) initialise(root);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }

  if (typeof MutationObserver === 'function') {
    new MutationObserver((records) => records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) initialiseAll(node);
      });
    })).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
